export function getRecordingConfig() {
  return {
    // react-scripts replaced process.env at build time; Vite leaves
    // `process` undefined in the browser, so these had to move to
    // import.meta.env. VITE_ is the new prefix; the REACT_APP_ reads are
    // kept as a transitional fallback (vite.config.ts still exposes that
    // prefix) so an existing local .env is not silently ignored. None of
    // these are set in any deployed environment, so the defaults are what
    // production has always used.
    mode: import.meta.env.VITE_RECORDING_MODE || import.meta.env.REACT_APP_RECORDING_MODE || "all",
    sampleRate: parseFloat(
      import.meta.env.VITE_RECORDING_SAMPLE_RATE ||
        import.meta.env.REACT_APP_RECORDING_SAMPLE_RATE ||
        "1.0",
    ),
    recorderUrl:
      import.meta.env.VITE_SESSION_RECORDER_URL ||
      import.meta.env.REACT_APP_SESSION_RECORDER_URL ||
      "/api/session-recorder",
    batchSize: 50,
    // RARE-321: batching on event count alone built bodies the recorder
    // refused with a 413, and the first batch of every recording carries an
    // rrweb full DOM snapshot, so the one that was always lost was the one
    // replay cannot start without. A flush is now split to fit this budget as
    // well as the count.
    //
    // Half of serverMaxBatchBytes, not all of it, so a tab left open across a
    // deploy that lowers the server limit still fits, and so the JSON envelope
    // around the events has room.
    maxBatchBytes: 2 * 1024 * 1024,
    // What the recorder itself will refuse: MAX_BATCH_SIZE_BYTES in
    // services/session-recorder/src/config.ts. Kept here rather than fetched
    // because ingest offers no way to ask. A single event above this is
    // dropped instead of sent, since one rrweb event cannot be split and the
    // request is certain to fail.
    serverMaxBatchBytes: 4 * 1024 * 1024,
    // RARE-303: what the buffer is allowed to hold before a flush is triggered,
    // and therefore the largest tail an unload can be left holding.
    //
    // The unload flush has to be a keepalive fetch to carry the bearer token
    // through the unload, and the Fetch standard caps the *total* inflight
    // keepalive body for the page at 64 KiB, shared across every such request.
    // That is why the ticket's first option, chunking the tail across several
    // requests, buys nothing: N requests of 60/N KiB put exactly as many bytes
    // on the wire as one request of 60 KiB, and pay N JSON envelopes to do it.
    // The cap is on bytes in flight, not on requests in flight. So this takes
    // the ticket's other option instead, and keeps the tail small enough during
    // the session that one request always carries it.
    //
    // 32 KiB rather than the whole 60 KiB budget, because the budget is shared
    // and this path overlaps itself: visibilitychange fires on a tab switch and
    // beforeunload can follow before that first request has settled, and Sentry
    // sends its own keepalive requests out of the same allowance. At 32 KiB a
    // tail still fits alongside another one its own size with room to spare.
    //
    // This is below maxBatchBytes, so it is the byte trigger that fires first
    // and maxBatchBytes no longer has a trigger role: it remains the budget a
    // flush is *split* against, for the requeued backlog that can exceed this.
    keepaliveTailBytes: 32 * 1024,
    flushIntervalMs: 10000,
    // A periodic flush that never settles holds the flush chain open forever,
    // and while it does the buffer only grows. Thirty seconds is generous for a
    // maxBatchBytes body on a slow connection and still well short of leaving
    // the chain wedged for the rest of the session. An abort is treated exactly
    // like any other non-delivery: the events go back and the next flush
    // carries them.
    flushRequestTimeoutMs: 30000,
    maxBufferSize: 500,
    // The second half of the buffer bound. maxBufferSize caps the buffer in
    // entries, which is no bound at all in bytes: 500 entries of a megabyte
    // each is half a gigabyte held in a tab. A server that rejects every flush
    // is exactly the case where requeueing pushes towards that, so the retained
    // backlog is capped in bytes too.
    //
    // Twice maxBatchBytes, because a flush is triggered at maxBatchBytes and a
    // refused one is handed straight back: one full buffer plus one full
    // requeued flush is the steady state of a persistently rejecting server,
    // and a healthy recorder never comes near it.
    maxBufferBytes: 4 * 1024 * 1024,
  };
}

export function shouldRecord(config) {
  if (config.mode === "off") return false;
  if (config.mode === "sampled") return Math.random() < config.sampleRate;
  return true;
}
